import { supabase } from './supabase';

const GOOGLE_BOOKS_BASE = 'https://www.googleapis.com/books/v1/volumes';

// ─── Google Books via Edge Function (keeps API key server-side) ───────────────

async function gbSearch(q) {
  const { data, error } = await supabase.functions.invoke('search-books', {
    body: { action: 'search', q },
  });
  if (error) throw error;
  return data;
}

async function gbGet(id) {
  const { data, error } = await supabase.functions.invoke('search-books', {
    body: { action: 'get', id },
  });
  if (error) throw error;
  return data;
}

// ─── Google Books helpers ─────────────────────────────────────────────────────

function mapGoogleBook(item) {
  const info = item.volumeInfo || {};
  const links = info.imageLinks || {};
  // Prefer highest-res cover available
  let cover = links.extraLarge || links.large || links.medium || links.small || links.thumbnail || links.smallThumbnail || '';
  if (cover) cover = cover.replace('http://', 'https://');
  let isbn = '';
  for (const ident of info.industryIdentifiers || []) {
    if (ident.type === 'ISBN_13' || ident.type === 'ISBN_10') {
      isbn = ident.identifier;
      if (ident.type === 'ISBN_13') break;
    }
  }
  return {
    google_books_id: item.id,
    title: info.title || 'Unknown Title',
    author: (info.authors || ['Unknown'])[0],
    // Extra display fields (not stored in DB, used for book page)
    all_authors: info.authors || [],
    isbn,
    cover_url: cover,
    description: info.description || '',
    page_count: info.pageCount || null,
    published_date: info.publishedDate || '',
    publisher: info.publisher || '',
    categories: info.categories || [],
    source: 'google',
  };
}

async function getOrCreateBook(googleBooksId) {
  const { data: existing } = await supabase
    .from('books')
    .select('*')
    .eq('google_books_id', googleBooksId)
    .maybeSingle();

  if (existing) return existing;

  const item = await gbGet(googleBooksId);
  const full = mapGoogleBook(item);

  // Store all available metadata
  const { data, error } = await supabase.from('books').insert({
    google_books_id: full.google_books_id,
    title: full.title,
    author: full.author,
    isbn: full.isbn,
    cover_url: full.cover_url,
    published_date: full.published_date,
    page_count: full.page_count,
    description: full.description,
    publisher: full.publisher,
  }).select().single();

  if (error) {
    const { data: retry } = await supabase
      .from('books').select('*').eq('google_books_id', googleBooksId).single();
    if (retry) return retry;
    throw error;
  }
  // Merge extra fields back for immediate use
  return { ...data, description: full.description, all_authors: full.all_authors, page_count: full.page_count, published_date: full.published_date, publisher: full.publisher };
}

// ─── Books ────────────────────────────────────────────────────────────────────

export const booksApi = {
  async search(q) {
    const data = await gbSearch(q);
    return (data.items || []).map(mapGoogleBook);
  },

  async searchLocal(q) {
    try {
      const { data, error } = await supabase
        .from('books')
        .select('*')
        .or(`title.ilike.%${q}%,author.ilike.%${q}%`)
        .limit(10);
      if (error) return [];
      return (data || []).map((book) => ({
        google_books_id: book.google_books_id || '',
        title: book.title || 'Unknown Title',
        author: book.author || 'Unknown',
        isbn: book.isbn || '',
        cover_url: book.cover_url || '',
        source: 'local',
      }));
    } catch {
      return [];
    }
  },

  async searchOpenLibrary(q) {
    try {
      const res = await fetch(
        `https://openlibrary.org/search.json?q=${encodeURIComponent(q)}&limit=6&fields=key,title,author_name,cover_i,isbn,first_publish_year`
      );
      if (!res.ok) return [];
      const data = await res.json();
      return (data.docs || []).map((doc) => ({
        google_books_id: `ol_${doc.key?.replace('/works/', '') || doc.cover_i || Math.random()}`,
        title: doc.title || 'Unknown Title',
        author: (doc.author_name || ['Unknown'])[0],
        all_authors: doc.author_name || [],
        isbn: (doc.isbn || [])[0] || '',
        cover_url: doc.cover_i
          ? `https://covers.openlibrary.org/b/id/${doc.cover_i}-L.jpg`
          : '',
        published_date: doc.first_publish_year ? String(doc.first_publish_year) : '',
        source: 'openlibrary',
      }));
    } catch {
      return [];
    }
  },

  // Store a book from any source into the DB, return canonical record
  async getOrCreate(book) {
    const { data: existing } = await supabase
      .from('books').select('*').eq('google_books_id', book.google_books_id).maybeSingle();
    if (existing) return existing;

    // For Google Books IDs, re-fetch to get highest quality metadata
    let toInsert = {
      google_books_id: book.google_books_id,
      title: book.title,
      author: book.author,
      isbn: book.isbn || '',
      cover_url: book.cover_url || '',
    };

    if (!book.google_books_id.startsWith('ol_') && !book.google_books_id.startsWith('manual_')) {
      try {
        const item = await gbGet(book.google_books_id);
        const fresh = mapGoogleBook(item);
        toInsert = { google_books_id: fresh.google_books_id, title: fresh.title, author: fresh.author, isbn: fresh.isbn, cover_url: fresh.cover_url };
      } catch {}
    }

    const { data, error } = await supabase.from('books').insert(toInsert).select().single();
    if (error) {
      const { data: retry } = await supabase.from('books').select('*').eq('google_books_id', book.google_books_id).maybeSingle();
      if (retry) return retry;
      throw error;
    }
    return data;
  },

  async createManual({ title, author, coverUrl = '' }) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const google_books_id = `manual_${crypto.randomUUID()}`;
    const { data, error } = await supabase
      .from('books')
      .insert({ google_books_id, title, author, cover_url: coverUrl })
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async importGoodreads(rows, onProgress) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const SHELF_MAP = { 'read': 'read', 'currently-reading': 'reading', 'to-read': 'want' };
    const results = { imported: 0, updated: 0, skipped: 0, failed: 0, byShelf: { read: 0, reading: 0, want: 0 } };

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      onProgress?.({ current: i + 1, total: rows.length, title: row.title, ...results });

      try {
        const isbn = (row.isbn13 || row.isbn || '').replace(/[="]/g, '').trim();
        const shelfState = SHELF_MAP[row.exclusiveShelf] || 'want';

        // 1. Try to find existing book by ISBN
        let book = null;
        if (isbn) {
          const { data } = await supabase.from('books').select('*').eq('isbn', isbn).maybeSingle();
          book = data;
        }

        // 2. Create a stub — Google Books data loads on demand when the book page is visited
        if (!book) {
          const google_books_id = `manual_${crypto.randomUUID()}`;
          const { data, error } = await supabase.from('books').insert({
            google_books_id,
            title: row.title || 'Unknown Title',
            author: row.author || 'Unknown Author',
            isbn: isbn || '',
          }).select().single();

          if (error) {
            // Possible race: try fetching again by isbn
            if (isbn) {
              const { data: retry } = await supabase.from('books').select('*').eq('isbn', isbn).maybeSingle();
              book = retry;
            }
            if (!book) throw error;
          } else {
            book = data;
          }
        }

        // 3. Check if already on shelf
        const { data: existing } = await supabase.from('shelf_items')
          .select('id,shelf_states,read_date').eq('user_id', user.id).eq('book_id', book.id).maybeSingle();

        // Prepare shelf item data
        const shelfData = {};
        
        // Set read_date if book was read and we have the date from Goodreads
        if (shelfState === 'read' && row.dateRead) {
          const parsedDate = new Date(row.dateRead);
          if (!isNaN(parsedDate.getTime())) {
            shelfData.read_date = parsedDate.toISOString();
          }
        }

        if (existing) {
          // Update existing shelf item ONLY if we have new read_date data and they don't
          if (shelfData.read_date && !existing.read_date) {
            // Only update read_date, don't modify shelf_states to preserve user's manual changes
            await supabase.from('shelf_items')
              .update({ read_date: shelfData.read_date, updated_at: new Date().toISOString() })
              .eq('id', existing.id);
            
            results.updated++;
          } else {
            results.skipped++;
          }
        } else {
          // 4. Add to shelf (new book)
          const shelfItem = {
            user_id: user.id, 
            book_id: book.id, 
            shelf_states: [shelfState],
            ...shelfData,
          };
          
          await supabase.from('shelf_items').insert(shelfItem);
          results.byShelf[shelfState] = (results.byShelf[shelfState] || 0) + 1;
          results.imported++;

          // 5. Import review/rating if book was read (only for NEW books)
          const rating = parseInt(row.myRating, 10) || 0;
          const reviewText = (row.myReview || '').replace(/<[^>]*>/g, '').trim();
          if (shelfState === 'read' && (rating > 0 || reviewText)) {
            // Check if review already exists
            const { data: existingReview } = await supabase.from('reviews')
              .select('id').eq('user_id', user.id).eq('book_id', book.id).maybeSingle();
            
            if (!existingReview) {
              await supabase.from('reviews').insert({
                user_id: user.id, book_id: book.id,
                body: reviewText, star_rating: rating,
              });
            }
          }
        }
      } catch (_) {
        results.failed++;
      }
    }

    onProgress?.({ current: rows.length, total: rows.length, ...results, done: true });
    return results;
  },

  async getBook(bookId) {
    // Try internal UUID first, then google_books_id
    const { data: byId } = await supabase
      .from('books')
      .select('*')
      .eq('id', bookId)
      .maybeSingle();

    const { data: byGoogleId } = !byId
      ? await supabase.from('books').select('*').eq('google_books_id', bookId).maybeSingle()
      : { data: null };

    let book = byId || byGoogleId;

    if (!book) {
      // Treat bookId as a google_books_id and fetch/create
      book = await getOrCreateBook(bookId);
    }

    const internalId = book.id;

    // Community stats
    const [readRes, wantRes, ownRes] = await Promise.all([
      supabase.from('shelf_items').select('*', { count: 'exact', head: true }).eq('book_id', internalId).eq('shelf_state', 'read'),
      supabase.from('shelf_items').select('*', { count: 'exact', head: true }).eq('book_id', internalId).eq('shelf_state', 'want'),
      supabase.from('shelf_items').select('*', { count: 'exact', head: true }).eq('book_id', internalId).eq('shelf_state', 'own'),
    ]);

    // Current user's shelf entry
    const { data: { user } } = await supabase.auth.getUser();
    let userShelfItem = null;
    if (user) {
      const { data } = await supabase
        .from('shelf_items')
        .select('*')
        .eq('book_id', internalId)
        .eq('user_id', user.id)
        .maybeSingle();
      userShelfItem = data;
    }

    // Augment with rich metadata from Google Books (description, page count, etc.)
    let enriched = {};
    if (book.google_books_id && !book.google_books_id.startsWith('ol_') && !book.google_books_id.startsWith('manual_')) {
      try {
        const item = await gbGet(book.google_books_id);
        const full = mapGoogleBook(item);
        enriched = {
          description: full.description,
          page_count: full.page_count,
          published_date: full.published_date,
          publisher: full.publisher,
          all_authors: full.all_authors,
          categories: full.categories,
        };
      } catch {}
    }

    return {
      ...book,
      ...enriched,
      read_count: readRes.count || 0,
      want_count: wantRes.count || 0,
      own_count: ownRes.count || 0,
      user_shelf_item: userShelfItem,
    };
  },
};

// ─── Shelf ────────────────────────────────────────────────────────────────────

export const shelfApi = {
  async getMyShelf() {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');
    const { data, error } = await supabase
      .from('shelf_items')
      .select('*, book:books(*)')
      .eq('user_id', user.id)
      .order('updated_at', { ascending: false });
    if (error) throw error;
    
    // Sort by published_date chronologically if available
    const sorted = (data || []).sort((a, b) => {
      const dateA = a.book?.published_date;
      const dateB = b.book?.published_date;
      
      // Books without dates go to the end
      if (!dateA && !dateB) return 0;
      if (!dateA) return 1;
      if (!dateB) return -1;
      
      // Compare dates (newer first)
      return dateB.localeCompare(dateA);
    });
    
    return sorted;
  },

  async getUserShelf(username) {
    const { data: profile, error: pErr } = await supabase
      .from('profiles')
      .select('id')
      .eq('username', username.toLowerCase())
      .single();
    if (pErr || !profile) throw new Error('User not found');
    const { data, error } = await supabase
      .from('shelf_items')
      .select('*, book:books(*)')
      .eq('user_id', profile.id)
      .order('updated_at', { ascending: false });
    if (error) throw error;
    
    // Sort by published_date chronologically if available
    const sorted = (data || []).sort((a, b) => {
      const dateA = a.book?.published_date;
      const dateB = b.book?.published_date;
      
      // Books without dates go to the end
      if (!dateA && !dateB) return 0;
      if (!dateA) return 1;
      if (!dateB) return -1;
      
      // Compare dates (newer first)
      return dateB.localeCompare(dateA);
    });
    
    return sorted;
  },

  async addBook({ google_books_id, shelf_states }) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const book = await getOrCreateBook(google_books_id);
    const now = new Date().toISOString();

    const { data, error } = await supabase
      .from('shelf_items')
      .insert({ user_id: user.id, book_id: book.id, shelf_states, created_at: now, updated_at: now })
      .select('*, book:books(*)')
      .single();
    if (error) throw error;

    // Feed item
    await supabase.from('feed_items').insert({
      type: 'shelf_add',
      user_id: user.id,
      book_id: book.id,
      shelf_states,
    });

    return data;
  },

  async updateBook(shelfItemId, { shelf_states }) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('shelf_items')
      .update({ shelf_states, updated_at: new Date().toISOString() })
      .eq('id', shelfItemId)
      .eq('user_id', user.id)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async removeBook(shelfItemId) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase
      .from('shelf_items')
      .delete()
      .eq('id', shelfItemId)
      .eq('user_id', user.id);
    if (error) throw error;
  },
};

// ─── Users ────────────────────────────────────────────────────────────────────

export const usersApi = {
  async getProfile(username) {
    const { data: profile, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('username', username.toLowerCase())
      .single();
    if (error || !profile) throw new Error('User not found');

    const [followerRes, followingRes, shelfStates, authUserRes] = await Promise.all([
      supabase.from('follows').select('*', { count: 'exact', head: true }).eq('following_id', profile.id),
      supabase.from('follows').select('*', { count: 'exact', head: true }).eq('follower_id', profile.id),
      supabase.from('shelf_items').select('shelf_states').eq('user_id', profile.id),
      supabase.auth.getUser(),
    ]);

    const shelfCounts = { read: 0, reading: 0, want: 0, own: 0, dnf: 0 };
    for (const item of shelfStates.data || []) {
      if (item.shelf_states && Array.isArray(item.shelf_states)) {
        for (const state of item.shelf_states) {
          if (shelfCounts[state] !== undefined) shelfCounts[state]++;
        }
      }
    }

    let is_following = false;
    const currentUser = authUserRes.data?.user;
    if (currentUser && currentUser.id !== profile.id) {
      const { data: followRow } = await supabase
        .from('follows')
        .select('id')
        .eq('follower_id', currentUser.id)
        .eq('following_id', profile.id)
        .maybeSingle();
      is_following = !!followRow;
    }

    return {
      ...profile,
      follower_count: followerRes.count || 0,
      following_count: followingRes.count || 0,
      shelf_counts: shelfCounts,
      is_following,
    };
  },

  async searchUsers(q) {
    if (!q.trim()) return [];
    const term = q.trim().toLowerCase();
    const { data: { user } } = await supabase.auth.getUser();

    const { data } = await supabase
      .from('profiles')
      .select('id, username, display_name, avatar_url, bio')
      .or(`username.ilike.%${term}%,display_name.ilike.%${term}%`)
      .limit(10);

    if (!data?.length) return [];

    let followingIds = new Set();
    if (user) {
      const { data: follows } = await supabase
        .from('follows')
        .select('following_id')
        .eq('follower_id', user.id)
        .in('following_id', data.map((p) => p.id));
      followingIds = new Set((follows || []).map((f) => f.following_id));
    }

    return data
      .filter((p) => !user || p.id !== user.id)
      .map((p) => ({ ...p, is_following: followingIds.has(p.id) }));
  },

  async follow(username) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');
    const { data: target } = await supabase.from('profiles').select('id').eq('username', username.toLowerCase()).single();
    if (!target) throw new Error('User not found');
    const { error } = await supabase.from('follows').insert({ follower_id: user.id, following_id: target.id });
    if (error) throw error;
    // Notify the followed user (fire-and-forget)
    if (target.id !== user.id) {
      supabase.from('notifications').insert({
        user_id: target.id, actor_id: user.id, type: 'follow',
      }).then(() => {});
      sendPush(target.id, 'New follower', `Someone started following you`, '/notifications');
    }
  },

  async unfollow(username) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');
    const { data: target } = await supabase.from('profiles').select('id').eq('username', username.toLowerCase()).single();
    if (!target) throw new Error('User not found');
    const { error } = await supabase.from('follows').delete().eq('follower_id', user.id).eq('following_id', target.id);
    if (error) throw error;
  },

  async getFollowers(username) {
    const { data: profile } = await supabase.from('profiles').select('id').eq('username', username.toLowerCase()).single();
    if (!profile) return [];
    const { data } = await supabase.from('follows').select('follower:profiles!follower_id(id,username,display_name,avatar_url)').eq('following_id', profile.id);
    return (data || []).map((r) => r.follower);
  },

  async getFollowing(username) {
    const { data: profile } = await supabase.from('profiles').select('id').eq('username', username.toLowerCase()).single();
    if (!profile) return [];
    const { data } = await supabase.from('follows').select('following:profiles!following_id(id,username,display_name,avatar_url)').eq('follower_id', profile.id);
    return (data || []).map((r) => r.following);
  },
};

// ─── Reviews ──────────────────────────────────────────────────────────────────

export const reviewsApi = {
  async getReviews(bookId) {
    const { data: book } = await supabase.from('books').select('id').or(`id.eq.${bookId},google_books_id.eq.${bookId}`).maybeSingle();
    if (!book) return [];
    const { data, error } = await supabase
      .from('reviews')
      .select('*, user:profiles!user_id(username,display_name,avatar_url)')
      .eq('book_id', book.id)
      .order('created_at', { ascending: false });
    if (error) throw error;
    return data || [];
  },

  async createReview(bookId, { body, star_rating }) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data: book } = await supabase.from('books').select('id').or(`id.eq.${bookId},google_books_id.eq.${bookId}`).maybeSingle();
    if (!book) throw new Error('Book not found');

    const { data: onShelf } = await supabase
      .from('shelf_items')
      .select('id')
      .eq('user_id', user.id)
      .eq('book_id', book.id)
      .eq('shelf_state', 'read')
      .maybeSingle();
    if (!onShelf) throw new Error('You can only review books you have marked as read');

    const { data, error } = await supabase
      .from('reviews')
      .insert({ user_id: user.id, book_id: book.id, body, star_rating })
      .select()
      .single();
    if (error) throw error;

    await supabase.from('feed_items').insert({ type: 'review', user_id: user.id, book_id: book.id, review_id: data.id });
    return data;
  },

  async getByUser(username) {
    const { data: profile } = await supabase.from('profiles').select('id').eq('username', username.toLowerCase()).single();
    if (!profile) return [];
    const { data, error } = await supabase
      .from('reviews')
      .select('*, book:books(id,title,cover_url,author)')
      .eq('user_id', profile.id)
      .order('created_at', { ascending: false });
    if (error) throw error;
    return data || [];
  },

  async markHelpful(reviewId) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data: existing } = await supabase
      .from('review_helpful')
      .select('id')
      .eq('user_id', user.id)
      .eq('review_id', reviewId)
      .maybeSingle();

    const { data: review } = await supabase.from('reviews').select('helpful_count').eq('id', reviewId).single();
    const current = review?.helpful_count || 0;

    if (existing) {
      await supabase.from('review_helpful').delete().eq('user_id', user.id).eq('review_id', reviewId);
      const newCount = Math.max(0, current - 1);
      await supabase.from('reviews').update({ helpful_count: newCount }).eq('id', reviewId);
      return { helpful: false, helpful_count: newCount };
    } else {
      await supabase.from('review_helpful').insert({ user_id: user.id, review_id: reviewId });
      const newCount = current + 1;
      await supabase.from('reviews').update({ helpful_count: newCount }).eq('id', reviewId);
      // Notify review author
      const { data: rev } = await supabase.from('reviews').select('user_id,book_id').eq('id', reviewId).single();
      if (rev && rev.user_id !== user.id) {
        supabase.from('notifications').insert({
          user_id: rev.user_id, actor_id: user.id, type: 'review_helpful',
          entity_id: reviewId, book_id: rev.book_id,
        }).then(() => {});
        sendPush(rev.user_id, 'Someone found your review helpful', 'Your review got a helpful mark', '/notifications');
      }
      return { helpful: true, helpful_count: newCount };
    }
  },
};

// ─── Reactions ────────────────────────────────────────────────────────────────

export const reactionsApi = {
  async getReactions(bookId) {
    const { data: book } = await supabase.from('books').select('id').or(`id.eq.${bookId},google_books_id.eq.${bookId}`).maybeSingle();
    if (!book) return [];
    const { data, error } = await supabase
      .from('reactions')
      .select('*, user:profiles!user_id(username,display_name,avatar_url)')
      .eq('book_id', book.id)
      .order('created_at', { ascending: false });
    if (error) throw error;
    return data || [];
  },

  async createReaction(bookId, { body, emoji }) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data: book } = await supabase.from('books').select('id').or(`id.eq.${bookId},google_books_id.eq.${bookId}`).maybeSingle();
    if (!book) throw new Error('Book not found');

    const { data, error } = await supabase
      .from('reactions')
      .insert({ user_id: user.id, book_id: book.id, body, emoji })
      .select()
      .single();
    if (error) throw error;

    await supabase.from('feed_items').insert({ type: 'reaction', user_id: user.id, book_id: book.id, reaction_id: data.id });
    return data;
  },

  async getByUser(username) {
    const { data: profile } = await supabase.from('profiles').select('id').eq('username', username.toLowerCase()).single();
    if (!profile) return [];
    const { data, error } = await supabase
      .from('reactions')
      .select('*, book:books(id,title,cover_url,author)')
      .eq('user_id', profile.id)
      .order('created_at', { ascending: false });
    if (error) throw error;
    return data || [];
  },

  async toggleLike(reactionId) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data: existing } = await supabase
      .from('reaction_likes')
      .select('id')
      .eq('user_id', user.id)
      .eq('reaction_id', reactionId)
      .maybeSingle();

    const { data: reaction } = await supabase.from('reactions').select('like_count').eq('id', reactionId).single();
    const current = reaction?.like_count || 0;

    if (existing) {
      await supabase.from('reaction_likes').delete().eq('user_id', user.id).eq('reaction_id', reactionId);
      const newCount = Math.max(0, current - 1);
      await supabase.from('reactions').update({ like_count: newCount }).eq('id', reactionId);
      return { liked: false, like_count: newCount };
    } else {
      await supabase.from('reaction_likes').insert({ user_id: user.id, reaction_id: reactionId });
      const newCount = current + 1;
      await supabase.from('reactions').update({ like_count: newCount }).eq('id', reactionId);
      // Notify reaction author
      const { data: rxn } = await supabase.from('reactions').select('user_id,book_id').eq('id', reactionId).single();
      if (rxn && rxn.user_id !== user.id) {
        supabase.from('notifications').insert({
          user_id: rxn.user_id, actor_id: user.id, type: 'reaction_like',
          entity_id: reactionId, book_id: rxn.book_id,
        }).then(() => {});
        sendPush(rxn.user_id, 'Someone liked your reaction', 'Your reaction got a like', '/notifications');
      }
      return { liked: true, like_count: newCount };
    }
  },
};

// ─── Push notifications ───────────────────────────────────────────────────────

export const pushApi = {
  async subscribe() {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return null;
    const vapidKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
    if (!vapidKey) return null;

    const reg = await navigator.serviceWorker.ready;
    const existing = await reg.pushManager.getSubscription();
    const sub = existing || await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidKey),
    });

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const key = sub.getKey('p256dh');
    const auth = sub.getKey('auth');

    await supabase.from('push_subscriptions').upsert({
      user_id: user.id,
      endpoint: sub.endpoint,
      p256dh: btoa(String.fromCharCode(...new Uint8Array(key))),
      auth: btoa(String.fromCharCode(...new Uint8Array(auth))),
    }, { onConflict: 'user_id,endpoint' });

    return sub;
  },

  async unsubscribe() {
    if (!('serviceWorker' in navigator)) return;
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (!sub) return;
    await sub.unsubscribe();
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      await supabase.from('push_subscriptions').delete()
        .eq('user_id', user.id).eq('endpoint', sub.endpoint);
    }
  },
};

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

// Helper: send a push notification to a user (fire-and-forget)
function sendPush(userId, title, body, url) {
  if (!process.env.REACT_APP_VAPID_PUBLIC_KEY) return; // push not configured
  supabase.functions.invoke('send-push', { body: { user_id: userId, title, body, url } }).then(() => {});
}

// ─── Notifications ────────────────────────────────────────────────────────────

export const notificationsApi = {
  async getAll() {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];
    const { data, error } = await supabase
      .from('notifications')
      .select('*, actor:profiles!actor_id(username,display_name,avatar_url), book:books(id,title,cover_url)')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(30);
    if (error) throw error;
    return data || [];
  },

  async getUnreadCount() {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return 0;
    const { count } = await supabase
      .from('notifications')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', user.id)
      .eq('read', false);
    return count || 0;
  },

  async markRead(ids) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    await supabase.from('notifications').update({ read: true }).in('id', ids).eq('user_id', user.id);
  },

  async markAllRead() {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    await supabase.from('notifications').update({ read: true }).eq('user_id', user.id).eq('read', false);
  },
};

// ─── Feed ─────────────────────────────────────────────────────────────────────

export const feedApi = {
  async getFeed(before) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    const { data: follows } = await supabase.from('follows').select('following_id').eq('follower_id', user.id);
    const followingIds = (follows || []).map((f) => f.following_id);
    if (!followingIds.length) return [];

    let query = supabase
      .from('feed_items')
      .select('*, user:profiles!user_id(username,display_name,avatar_url), book:books(*)')
      .in('user_id', followingIds)
      .order('created_at', { ascending: false })
      .limit(20);

    if (before) query = query.lt('created_at', before);

    const { data: items, error } = await query;
    if (error) throw error;

    // Enrich type-specific content
    const enriched = await Promise.all(
      (items || []).map(async (item) => {
        if (item.type === 'review' && item.review_id) {
          const { data: review } = await supabase.from('reviews').select('body,star_rating').eq('id', item.review_id).single();
          return { ...item, review };
        }
        if (item.type === 'reaction' && item.reaction_id) {
          const { data: reaction } = await supabase.from('reactions').select('body,emoji').eq('id', item.reaction_id).single();
          return { ...item, reaction };
        }
        if (item.type === 'discussion' && item.discussion_id) {
          const { data: discussion } = await supabase.from('book_discussions').select('body,tag').eq('id', item.discussion_id).single();
          return { ...item, discussion };
        }
        if (item.type === 'discussion_reply' && item.reply_id) {
          const { data: discussion_reply } = await supabase.from('discussion_replies').select('body').eq('id', item.reply_id).single();
          return { ...item, discussion_reply };
        }
        return item;
      })
    );

    return enriched;
  },
};

// ─── Discussions ──────────────────────────────────────────────────────────────

export const discussionsApi = {
  async getPosts(bookId) {
    const { data: { user } } = await supabase.auth.getUser();

    const { data, error } = await supabase
      .from('book_discussions')
      .select(`
        id, body, tag, created_at, user_id,
        author:profiles!user_id(id, username, display_name, avatar_url),
        like_count:discussion_likes(count),
        reply_count:discussion_replies(count)
      `)
      .eq('book_id', bookId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    const posts = data || [];
    if (!posts.length) return [];

    // Current user's liked post IDs
    let likedPostIds = new Set();
    if (user) {
      const { data: myLikes } = await supabase
        .from('discussion_likes')
        .select('post_id')
        .eq('user_id', user.id)
        .in('post_id', posts.map((p) => p.id))
        .not('post_id', 'is', null);
      likedPostIds = new Set((myLikes || []).map((l) => l.post_id));
    }

    // Author shelf states for this book (context display)
    const userIds = [...new Set(posts.map((p) => p.user_id))];
    const { data: shelfItems } = await supabase
      .from('shelf_items')
      .select('user_id, shelf_states')
      .eq('book_id', bookId)
      .in('user_id', userIds);
    const shelfByUser = Object.fromEntries((shelfItems || []).map((s) => [s.user_id, s.shelf_states?.[0] || null]));

    return posts.map((post) => ({
      ...post,
      like_count: post.like_count?.[0]?.count || 0,
      reply_count: post.reply_count?.[0]?.count || 0,
      liked: likedPostIds.has(post.id),
      author_shelf_state: shelfByUser[post.user_id] || null,
    }));
  },

  async createPost(bookId, { body, tag = 'no_spoilers' }) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('book_discussions')
      .insert({ book_id: bookId, user_id: user.id, body, tag })
      .select()
      .single();
    if (error) throw error;

    await supabase.from('feed_items').insert({
      type: 'discussion',
      user_id: user.id,
      book_id: bookId,
      discussion_id: data.id,
    });

    return data;
  },

  async getReplies(postId) {
    const { data: { user } } = await supabase.auth.getUser();

    const { data, error } = await supabase
      .from('discussion_replies')
      .select(`
        id, body, created_at, user_id,
        author:profiles!user_id(id, username, display_name, avatar_url),
        like_count:discussion_likes(count)
      `)
      .eq('post_id', postId)
      .order('created_at', { ascending: true });

    if (error) throw error;
    const replies = data || [];
    if (!replies.length) return [];

    let likedReplyIds = new Set();
    if (user) {
      const { data: myLikes } = await supabase
        .from('discussion_likes')
        .select('reply_id')
        .eq('user_id', user.id)
        .in('reply_id', replies.map((r) => r.id))
        .not('reply_id', 'is', null);
      likedReplyIds = new Set((myLikes || []).map((l) => l.reply_id));
    }

    return replies.map((r) => ({
      ...r,
      like_count: r.like_count?.[0]?.count || 0,
      liked: likedReplyIds.has(r.id),
    }));
  },

  async createReply(postId, bookId, { body }) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('discussion_replies')
      .insert({ post_id: postId, user_id: user.id, body })
      .select()
      .single();
    if (error) throw error;

    // Notify post author (skip self-replies)
    const { data: post } = await supabase
      .from('book_discussions')
      .select('user_id')
      .eq('id', postId)
      .single();
    if (post && post.user_id !== user.id) {
      await supabase.from('notifications').insert({
        user_id: post.user_id,
        actor_id: user.id,
        type: 'discussion_reply',
        book_id: bookId,
        entity_id: postId,
      });
    }

    await supabase.from('feed_items').insert({
      type: 'discussion_reply',
      user_id: user.id,
      book_id: bookId,
      discussion_id: postId,
      reply_id: data.id,
    });

    return data;
  },

  async toggleLike(type, id) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const field = type === 'post' ? 'post_id' : 'reply_id';
    const { data: existing } = await supabase
      .from('discussion_likes')
      .select('id')
      .eq('user_id', user.id)
      .eq(field, id)
      .maybeSingle();

    if (existing) {
      await supabase.from('discussion_likes').delete().eq('id', existing.id);
      return false;
    } else {
      await supabase.from('discussion_likes').insert({ user_id: user.id, [field]: id });
      return true;
    }
  },
};

// ─── Reading Goals ────────────────────────────────────────────────────────────

export const goalsApi = {
  async getGoal(username, year = new Date().getFullYear()) {
    const { data: profile } = await supabase
      .from('profiles').select('id').eq('username', username.toLowerCase()).single();
    if (!profile) return null;

    const { data: goal } = await supabase
      .from('reading_goals')
      .select('*')
      .eq('user_id', profile.id)
      .eq('year', year)
      .maybeSingle();

    if (!goal) return null;

    // Count books marked as read this year (using read_date)
    const startOfYear = `${year}-01-01T00:00:00.000Z`;
    const endOfYear = `${year}-12-31T23:59:59.999Z`;
    const { count } = await supabase
      .from('shelf_items')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', profile.id)
      .contains('shelf_states', ['read'])
      .gte('read_date', startOfYear)
      .lte('read_date', endOfYear);

    return { ...goal, progress: count || 0 };
  },

  async setGoal(year, target) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');
    const { data, error } = await supabase
      .from('reading_goals')
      .upsert({ user_id: user.id, year, target, updated_at: new Date().toISOString() }, { onConflict: 'user_id,year' })
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async deleteGoal(year) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');
    await supabase.from('reading_goals').delete().eq('user_id', user.id).eq('year', year);
  },

  async getHistoricalProgress(username) {
    const { data: profile } = await supabase
      .from('profiles').select('id,created_at').eq('username', username.toLowerCase()).single();
    if (!profile) return [];

    // Get the earliest read_date to determine how far back to look
    const { data: earliestBook } = await supabase
      .from('shelf_items')
      .select('read_date')
      .eq('user_id', profile.id)
      .contains('shelf_states', ['read'])
      .not('read_date', 'is', null)
      .order('read_date', { ascending: true })
      .limit(1)
      .maybeSingle();

    if (!earliestBook?.read_date) return [];

    const earliestYear = new Date(earliestBook.read_date).getFullYear();
    const currentYear = new Date().getFullYear();
    
    // Get current year's goal to use as target for historical years
    const { data: currentGoal } = await supabase
      .from('reading_goals')
      .select('target')
      .eq('user_id', profile.id)
      .eq('year', currentYear)
      .maybeSingle();

    const target = currentGoal?.target || 0;
    if (!target) return [];

    const historicalData = [];
    
    // Calculate progress for each year from earliest to current year - 1
    for (let year = earliestYear; year < currentYear; year++) {
      const startOfYear = `${year}-01-01T00:00:00.000Z`;
      const endOfYear = `${year}-12-31T23:59:59.999Z`;
      
      const { count } = await supabase
        .from('shelf_items')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', profile.id)
        .contains('shelf_states', ['read'])
        .gte('read_date', startOfYear)
        .lte('read_date', endOfYear);

      if (count > 0) {
        historicalData.push({
          year,
          progress: count || 0,
          target,
          percentage: Math.round((count / target) * 100),
        });
      }
    }

    return historicalData.reverse(); // Most recent first
  },
};

// ─── Culture Feed (RSS) ──────────────────────────────────────────────────────

export const cultureFeedApi = {
  async getItems() {
    // Try edge function first (triggers refresh if stale)
    try {
      const { data, error } = await supabase.functions.invoke('fetch-culture-feed', {
        body: { action: 'get' },
      });
      if (!error && data && data.length > 0) return data;
    } catch (_) {}

    // Fallback: read directly from cache table
    const { data } = await supabase
      .from('rss_cache')
      .select('*')
      .order('published_at', { ascending: false })
      .limit(15);
    return data || [];
  },
};

// ─── Curated featured books (fills discovery when DB is sparse) ───────────────

// A mix of Nigerian/African lit, global classics, and popular non-fiction
const CURATED_SEARCHES = [
  'Things Fall Apart Chinua Achebe',
  'Americanah Chimamanda Ngozi Adichie',
  'Half of a Yellow Sun',
  'Purple Hibiscus',
  'The Famished Road Ben Okri',
  'Open City Teju Cole',
  'Stay With Me Ayobami Adebayo',
  'Season of Migration North Tayeb Salih',
  'Homegoing Yaa Gyasi',
  'An Orchestra of Minorities Chigozie Obioma',
  'The Alchemist Paulo Coelho',
  'Atomic Habits James Clear',
  'Educated Tara Westover',
  'A Little Life Hanya Yanagihara',
  'Normal People Sally Rooney',
];

const FEATURED_CACHE_KEY = 'rtr_featured_books_v1';
const FEATURED_CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours

async function getFeaturedFromApis(needed) {
  // Check cache
  try {
    const raw = localStorage.getItem(FEATURED_CACHE_KEY);
    if (raw) {
      const { ts, books } = JSON.parse(raw);
      if (Date.now() - ts < FEATURED_CACHE_TTL && books.length > 0) return books;
    }
  } catch {}

  // Pick 4 random queries and run them in parallel
  const picks = [...CURATED_SEARCHES]
    .sort(() => Math.random() - 0.5)
    .slice(0, 4);

  const batches = await Promise.all(
    picks.map((q) =>
      gbSearch(q)
        .then((data) => (data.items || []).slice(0, 4).map(mapGoogleBook))
        .catch(() => [])
    )
  );

  // Flatten, dedup by google_books_id
  const seen = new Set();
  const books = batches
    .flat()
    .filter((b) => {
      if (!b.google_books_id || seen.has(b.google_books_id)) return false;
      seen.add(b.google_books_id);
      return true;
    })
    .slice(0, 16); // cache a few extra

  try {
    localStorage.setItem(FEATURED_CACHE_KEY, JSON.stringify({ ts: Date.now(), books }));
  } catch {}

  return books;
}

// ─── Discovery ────────────────────────────────────────────────────────────────

export const discoveryApi = {
  async getUsers() {
    const { data: { user } } = await supabase.auth.getUser();

    // Get a larger pool of users to randomize from
    const { data: profiles } = await supabase
      .from('profiles')
      .select('id,username,display_name,avatar_url')
      .limit(50);

    let followingIds = new Set();
    if (user) {
      const { data: follows } = await supabase.from('follows').select('following_id').eq('follower_id', user.id);
      followingIds = new Set((follows || []).map((f) => f.following_id));
    }

    // Filter out current user and already-followed users
    const unfollowedProfiles = (profiles || [])
      .filter((p) => !user || (p.id !== user.id && !followingIds.has(p.id)));

    // If we have fewer than 8 unfollowed users, include some followed ones
    const allProfiles = unfollowedProfiles.length >= 8 
      ? unfollowedProfiles 
      : (profiles || []).filter((p) => !user || p.id !== user.id);

    const withCounts = await Promise.all(
      allProfiles.map(async (p) => {
        const { count } = await supabase.from('follows').select('*', { count: 'exact', head: true }).eq('following_id', p.id);
        return { ...p, follower_count: count || 0, is_following: followingIds.has(p.id) };
      })
    );

    // Randomize the results while giving slight preference to users with more followers
    const shuffled = withCounts
      .sort(() => Math.random() - 0.5) // Random shuffle
      .sort((a, b) => {
        // Boost popular users slightly but keep randomness
        const aScore = a.follower_count + Math.random() * 10;
        const bScore = b.follower_count + Math.random() * 10;
        return bScore - aScore;
      })
      .slice(0, 8);

    return shuffled;
  },

  async getBooks() {
    // Get a larger pool of books to randomize from
    const { data: dbBooks } = await supabase.from('books').select('*').limit(50);

    const withCounts = await Promise.all(
      (dbBooks || []).map(async (b) => {
        const { count } = await supabase
          .from('shelf_items')
          .select('*', { count: 'exact', head: true })
          .eq('book_id', b.id);
        return { ...b, shelf_count: count || 0 };
      })
    );

    // Randomize while giving preference to popular books
    const popular = withCounts
      .sort(() => Math.random() - 0.5) // Random shuffle
      .sort((a, b) => {
        // Boost popular books but keep some randomness
        const aScore = a.shelf_count + Math.random() * 5;
        const bScore = b.shelf_count + Math.random() * 5;
        return bScore - aScore;
      })
      .slice(0, 12);

    if (popular.length >= 12) return popular;

    // 2. Fill remaining slots with curated books from Google Books
    const needed = 12 - popular.length;
    const featured = await getFeaturedFromApis(needed);

    // Remove any that are already in the DB list
    const dbGbIds = new Set(popular.map((b) => b.google_books_id).filter(Boolean));
    const extras = featured
      .filter((b) => !dbGbIds.has(b.google_books_id))
      .slice(0, needed);

    return [...popular, ...extras];
  },
};
